/**
 * The pieces every screen is built from.
 *
 * Small on purpose. A governed console mostly shows states, evidence and
 * tables, so these are the shapes that recur — and keeping them in one place is
 * what stops "pending" being amber here and orange there.
 */

import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

import type { PagedCollection } from "../api/useCollection";

export type Tone = "ok" | "wait" | "bad" | "busy" | "mind" | "quiet";

/**
 * How deep a card's title sits.
 *
 * The route shell renders the page title as the `h1`, so a card placed
 * directly on a page is a section under it and its title is an `h2` — which is
 * every card the product currently renders. The level is settable rather than
 * fixed because the moment a card is nested inside a titled section, a second
 * `h2` inside the first would describe a structure that is not there, and the
 * fix has to be available at the call site that knows.
 */
export type HeadingLevel = 2 | 3 | 4 | 5 | 6;

export function Card({
  title,
  actions,
  children,
  bodyless,
  headingLevel = 2,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  bodyless?: boolean;
  headingLevel?: HeadingLevel;
}) {
  // A card title looked like a heading and was a `<span>`, so a page of six
  // cards offered nothing to move between: assistive technology saw one
  // heading, the route title, and then an undifferentiated run of text.
  const Heading = `h${headingLevel}` as const;
  return (
    <section className="nx-card">
      {title ? (
        <header className="nx-card__head">
          <Heading className="nx-card__title">{title}</Heading>
          {actions ? <span style={{ marginLeft: "auto" }}>{actions}</span> : null}
        </header>
      ) : null}
      {bodyless ? children : <div className="nx-card__body">{children}</div>}
    </section>
  );
}

/**
 * A count that came from a page, told as the lower bound it is.
 *
 * A screen that reads one page of two hundred and reports `200` has stated
 * something the platform never said. What the platform said was "here are two
 * hundred, and there are more" — so the honest rendering is `200+`, and the
 * distinction belongs in one place rather than in each screen's own arithmetic.
 *
 * The bound is derived from the pagination's own `has_more`, never from a
 * separate flag a screen maintains alongside it. Two sources of truth about
 * whether a collection was cut short is how one of them ends up wrong.
 */
export function LowerBound({ count, hasMore }: { count: number; hasMore: boolean }) {
  if (!hasMore) return <span>{count}</span>;
  return <span title={`At least ${count}`}>{count}+</span>;
}

/**
 * Where a `More` sits, and therefore what has to supply its inset.
 *
 * This is a closed set rather than a `style` prop because an escape hatch is
 * how eleven copies of this component came to disagree about padding in the
 * first place: each screen was free to be locally right, and the three answers
 * were never compared. The values are derived from how `Card` actually renders,
 * not chosen by eye.
 *
 * `in-body` — inside a `<Card>` that laid out its own body, or a `TabPanel`,
 * which renders one. The body already supplies the 18px inset, so all that is
 * wanted here is separation from whatever the control follows.
 *
 * `bodyless` — inside a `<Card bodyless>`, where nothing supplies the inset,
 * because the card's child (a table, a timeline) is going edge to edge on
 * purpose. The padding reproduces what `nx-card__body` would have given — 18px
 * on the sides and below — plus the same separation above that `in-body` adds.
 *
 * `bodyless-followed` — the same, minus the bottom inset, for a bodyless card
 * where the block immediately after this one brings its own top inset. Two
 * blocks that each pay for the gap between them pay for it twice. This is
 * deliberately not the default for "something follows": most of what follows a
 * `More` in this product is a form padded `0 18px 18px`, written that way
 * precisely because the block above it supplies the separation. Whether the
 * bottom inset belongs here is a question about the next sibling, and it has to
 * be answered by looking at the next sibling.
 *
 * `flush` — the parent is already a grid that spaces its own children, so a
 * margin here would be counted twice.
 */
export type MorePlacement = "in-body" | "bodyless" | "bodyless-followed" | "flush";

const MORE_PLACEMENT: Record<MorePlacement, CSSProperties> = {
  "in-body": { marginTop: 12 },
  bodyless: { padding: "12px 18px 18px" },
  "bodyless-followed": { padding: "12px 18px 0" },
  flush: {},
};

/**
 * The rest of a collection, and — more importantly — that there is a rest.
 *
 * A table that stops at the page boundary and offers only a button has told the
 * reader nothing: a control is an affordance, not a statement, and fifty rows
 * under a heading read as the fifty rows there are. What the platform actually
 * said is weaker — that it returned as many as it was asked for and holds more.
 * So the fact is stated in words beside the control, and neither the sentence
 * nor the button appears once the platform has said the collection is whole.
 *
 * Ten screens each wrote this, and by the second review they disagreed about
 * padding, about whether the button's words were a prop, and about the verb.
 * The claim is the same claim wherever it is made, so — like `LowerBound` — it
 * belongs in one place rather than in each screen's own arithmetic.
 *
 * The collection is taken whole rather than as a `hasMore` and a `more`
 * handed in separately: two sources of truth about whether a list was cut short
 * is how one of them ends up pointing at a different collection than the other.
 * Nothing here reads the items, hence `unknown`.
 */
export function More({
  collection,
  label,
  hint,
  placement = "in-body",
}: {
  collection: PagedCollection<unknown>;
  label: string;
  hint: string;
  placement?: MorePlacement;
}) {
  if (!collection.hasMore) return null;
  return (
    <div style={{ display: "grid", gap: 6, ...MORE_PLACEMENT[placement] }}>
      <p className="nx-hint" style={{ margin: 0 }}>
        {hint}
      </p>
      <div>
        <button type="button" className="nx-button" onClick={collection.more}>
          {label}
        </button>
      </div>
    </div>
  );
}

const OUTCOME_TONE: Record<string, Tone> = {
  succeeded: "ok",
  allowed: "ok",
  allow: "ok",
  denied: "bad",
  deny: "bad",
  failed: "bad",
  refused: "bad",
};

/**
 * The tone a recorded outcome carries.
 *
 * Shared because the audit trail is reported in more than one place — the
 * explorer in full, Home in miniature — and two tables of outcome colours would
 * eventually disagree about what "denied" looks like. It lived in the explorer
 * and Home imported it from there, which made a summary card on the front page
 * depend on a whole other route.
 *
 * An outcome nobody has taught it stays quiet rather than guessing at alarm.
 */
export function outcomeTone(outcome: string): Tone {
  return OUTCOME_TONE[outcome] ?? "quiet";
}

export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`nx-pill nx-pill--${tone}`}>
      <span className="nx-dot" aria-hidden="true" />
      {children}
    </span>
  );
}

/** Classification is shown wherever the data is, because it travels with it. */
export function Classification({ value }: { value: string }) {
  const restricted = new Set([
    "quality",
    "regulatory",
    "pii",
    "phi",
    "executive",
    "safety_restricted",
  ]);
  const tone: Tone = restricted.has(value) ? "wait" : value === "confidential" ? "mind" : "quiet";
  return (
    <span className={`nx-chip nx-pill--${tone}`} title={`Data classification: ${value}`}>
      {value.replace(/_/g, " ")}
    </span>
  );
}

export function Facts({ children }: { children: ReactNode }) {
  return <dl className="nx-facts">{children}</dl>;
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function Banner({
  tone = "busy",
  title,
  children,
}: {
  tone?: "busy" | "wait" | "bad";
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className={`nx-banner${tone === "busy" ? "" : ` nx-banner--${tone}`}`} role="status">
      <div>
        <p className="nx-banner__title">{title}</p>
        {children ? <div className="nx-banner__body">{children}</div> : null}
      </div>
    </div>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="nx-mono">{children}</span>;
}

/**
 * A correlation id, as a way into the evidence.
 *
 * The connective tissue of the console: Nexus stamps one on every response,
 * every event and every decision, so the same id an operator is looking at is
 * the id that reconstructs what happened. Rendering it as text and leaving
 * somebody to copy it into a search box is how an evidence trail stays
 * theoretical.
 */
export function CorrelationLink({ value }: { value: string }) {
  return (
    <Link to={`/operations/audit/${encodeURIComponent(value)}`} title={`Audit for ${value}`}>
      <Identifier value={value} />
    </Link>
  );
}


/** Long identifiers, shortened for reading but never for copying. */
export function Identifier({ value }: { value: string }) {
  return (
    <span className="nx-mono" title={value}>
      {value.length > 14 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value}
    </span>
  );
}

export function Tabs({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: { id: string; label: string }[];
  active: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  return (
    <div className="nx-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={active === tab.id}
          aria-controls={`panel-${tab.id}`}
          className="nx-tab"
          onClick={() => {
            onSelect(tab.id);
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ id, active, children }: { id: string; active: string; children: ReactNode }) {
  if (id !== active) return null;
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className="nx-card__body">
      {children}
    </div>
  );
}

export function when(date: string | null | undefined): string {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
